/**
 * HikVision Alarm Types
 * 
 * Type definitions for alarm polling feature
 */

export interface Alarm {
  event_id: number;
  event_camera: string;
  event_company: number;
  event_location: number;
  event_type: string;
  event_tag: string;
  event_state: string;
  event_source: string;
  alarm_type: string;  // '10002', '100657', etc.
  alarm_priority: 'low' | 'medium' | 'high' | 'critical';
  cloud_alarm_id: string;
  cloud_device_id: string;
  event_note?: string;
  event_snapshot_url?: string;  // Snapshot/picture URL from HikVision
  event_video_url?: string;     // Video URL from HikVision
  created_date: string;  // ISO 8601 format
  processed: boolean;
  acknowledged: boolean;
  acknowledged_by?: number;
  acknowledged_at?: string;
}

export interface AlarmPollStatus {
  location_id: number;
  location_name: string;
  cloud_provider: string;
  alarm_polling: {
    enabled: boolean;
    subscription_active: boolean;
    last_success: string | null;
    last_error: string | null;
    error_code: string | null;
    has_credentials: boolean;
    is_healthy: boolean;
    last_success_recent?: boolean;
    next_retry_at?: string | null;
    consecutive_failures?: number;
  };
}

export interface AlarmFilters {
  page?: number;
  page_size?: number;
  alarm_type?: string;
  alarm_priority?: 'low' | 'medium' | 'high' | 'critical';
  start_date?: string;
  end_date?: string;
}

export interface AlarmListResponse {
  success: boolean;
  data: Alarm[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

export interface AlarmTestResult {
  success: boolean;
  location_id: number;
  overall_success: boolean;
  polling_retry_scheduled?: boolean;
  tests: Array<{
    name: string;
    passed: boolean;
    message?: string;
    error?: string;
  }>;
}

// Alarm type code to human-readable name mapping
export const ALARM_TYPE_NAMES: Record<string, string> = {
  '10000': 'Video Loss',
  '10001': 'Video Tampering',
  '10002': 'Motion Detection',
  '10003': 'Line Crossing',
  '10004': 'Intrusion',
  '10005': 'Object Removal',
  '10006': 'Object Left Behind',
  '10007': 'Audio Exception',
  '10018': 'Face Recognition',
  '100657': 'Intrusion Detection',
  '110013': 'Door Access',
};

// Alarm priority color mapping
export const ALARM_PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-blue-100 text-blue-800 border-blue-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  critical: 'bg-red-100 text-red-800 border-red-200',
};

// Alarm priority icons
export const ALARM_PRIORITY_ICONS: Record<string, string> = {
  low: '🔵',
  medium: '🟡',
  high: '🟠',
  critical: '🔴',
};
